import { useState, useEffect } from 'react';
import { Navigation } from '../components/Navigation';
import { FloatingBackButton } from '../components/FloatingBackButton';
import { useAuthStore } from '../store/authStore';
import { supabase } from '../lib/supabase';
import { Check, Zap, Crown, Building, Star, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';

interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  plan_type: string;
  price_monthly: number;
  price_yearly: number;
  features: string[];
  popular: boolean;
  display_order: number;
}

export function SubscriptionPricing() {
  const { user, profile } = useAuthStore();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [selectedPlanType, setSelectedPlanType] = useState<'job_seeker' | 'employer'>('job_seeker');

  useEffect(() => {
    if (profile) {
      setSelectedPlanType(profile.role === 'employer' ? 'employer' : 'job_seeker');
    }
    loadPlans();
  }, [profile]);

  const loadPlans = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('active', true)
        .order('display_order');

      if (error) throw error;
      setPlans(data || []);
    } catch (error) {
      console.error('Error loading plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPlans = plans.filter(
    plan => plan.plan_type === selectedPlanType || plan.plan_type === 'both'
  );

  const getPrice = (plan: SubscriptionPlan) => {
    const price = billingCycle === 'yearly' ? plan.price_yearly : plan.price_monthly;
    return (price / 100).toFixed(2);
  };

  const getPriceLabel = (plan: SubscriptionPlan) => {
    if (plan.price_monthly === 0) return 'Free';
    if (billingCycle === 'yearly') {
      const monthlyEquivalent = plan.price_yearly / 12 / 100;
      return `$${monthlyEquivalent.toFixed(2)}/mo`;
    }
    return `$${getPrice(plan)}/mo`;
  };

  const getSavings = (plan: SubscriptionPlan) => {
    if (billingCycle === 'yearly' && plan.price_yearly > 0) {
      const yearlyTotal = plan.price_monthly * 12;
      const savings = ((yearlyTotal - plan.price_yearly) / yearlyTotal * 100).toFixed(0);
      return `Save ${savings}%`;
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      <Navigation />
      <FloatingBackButton />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Choose Your Plan
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Unlock premium features and accelerate your career journey
          </p>
        </div>

        {/* Plan Type Selector */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex bg-white rounded-lg p-1 shadow-sm border-2 border-gray-200">
            <button
              onClick={() => setSelectedPlanType('job_seeker')}
              className={`px-6 py-3 rounded-md font-medium transition-all ${
                selectedPlanType === 'job_seeker'
                  ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white'
                  : 'text-gray-700 hover:text-gray-900'
              }`}
            >
              <Zap className="w-5 h-5 inline mr-2" />
              Job Seeker
            </button>
            <button
              onClick={() => setSelectedPlanType('employer')}
              className={`px-6 py-3 rounded-md font-medium transition-all ${
                selectedPlanType === 'employer'
                  ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white'
                  : 'text-gray-700 hover:text-gray-900'
              }`}
            >
              <Building className="w-5 h-5 inline mr-2" />
              Employer
            </button>
          </div>
        </div>

        {/* Billing Cycle Toggle */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-white rounded-lg p-1 shadow-sm border-2 border-gray-200">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-md font-medium transition-all ${
                billingCycle === 'monthly'
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-700 hover:text-gray-900'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-6 py-2 rounded-md font-medium transition-all flex items-center gap-2 ${
                billingCycle === 'yearly'
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-700 hover:text-gray-900'
              }`}
            >
              Yearly
              <span className="px-2 py-0.5 bg-green-500 text-white text-xs rounded-full">
                Save 50%
              </span>
            </button>
          </div>
        </div>

        {/* Plans Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {filteredPlans.map(plan => (
              <PlanCard
                key={plan.id}
                plan={plan}
                billingCycle={billingCycle}
                priceLabel={getPriceLabel(plan)}
                savings={getSavings(plan)}
                isCurrentUser={!!user}
              />
            ))}
          </div>
        )}

        {/* Job Boost Section (for Employers) */}
        {selectedPlanType === 'employer' && (
          <div className="mt-16">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Job Boost Options</h2>
              <p className="text-gray-600">Increase visibility and attract top talent faster</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <JobBoostCard
                name="Basic Boost"
                price="15"
                duration="7 days"
                multiplier="1.5x"
                features={[
                  '1.5x visibility in search',
                  '7-day promotion',
                  'Standard placement',
                  'Basic analytics',
                ]}
                color="from-blue-500 to-cyan-500"
              />
              <JobBoostCard
                name="Premium Boost"
                price="35"
                duration="7 days"
                multiplier="2x"
                features={[
                  '2x visibility in search',
                  'Highlighted listing',
                  '"Hot Job" badge',
                  'Advanced analytics',
                  'Email to matched candidates',
                ]}
                color="from-purple-500 to-pink-500"
                popular
              />
              <JobBoostCard
                name="Featured Boost"
                price="50"
                duration="7 days"
                multiplier="3x"
                features={[
                  '3x visibility in search',
                  'Top of search results',
                  '"Featured" badge',
                  'Premium analytics',
                  'Priority matching',
                  'Dedicated support',
                ]}
                color="from-orange-500 to-red-500"
              />
            </div>
          </div>
        )}

        {/* FAQ Section */}
        <div className="mt-16 max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-center text-gray-900 mb-8">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            <FAQItem
              question="Can I cancel my subscription anytime?"
              answer="Yes! You can cancel your subscription at any time. You'll continue to have access until the end of your billing period."
            />
            <FAQItem
              question="What payment methods do you accept?"
              answer="We accept all major credit cards (Visa, Mastercard, American Express) and PayPal through our secure payment processor, Stripe."
            />
            <FAQItem
              question="How do job boosts work?"
              answer="Job boosts increase your job posting's visibility in search results. Higher boost tiers provide more visibility, special badges, and additional features like top placement and priority matching."
            />
            <FAQItem
              question="Can I upgrade or downgrade my plan?"
              answer="Absolutely! You can upgrade or downgrade your plan at any time. Changes take effect immediately, and we'll prorate the difference."
            />
          </div>
        </div>
      </main>
    </div>
  );
}

interface PlanCardProps {
  plan: SubscriptionPlan;
  billingCycle: 'monthly' | 'yearly';
  priceLabel: string;
  savings: string | null;
  isCurrentUser: boolean;
}

function PlanCard({ plan, billingCycle, priceLabel, savings, isCurrentUser }: PlanCardProps) {
  const isPro = plan.name.toLowerCase().includes('pro');
  const isFree = plan.price_monthly === 0;

  return (
    <div
      className={`relative rounded-2xl p-8 transition-all ${
        plan.popular
          ? 'bg-gradient-to-br from-purple-600 to-blue-600 text-white shadow-2xl scale-105 border-4 border-purple-300'
          : 'bg-white border-2 border-gray-200 hover:border-purple-300 hover:shadow-xl'
      }`}
    >
      {plan.popular && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
          <div className="px-4 py-1 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-sm font-bold rounded-full flex items-center gap-1">
            <Star className="w-4 h-4" />
            Most Popular
          </div>
        </div>
      )}

      <div className="text-center mb-6">
        {isPro && <Crown className="w-12 h-12 mx-auto mb-3 text-yellow-400" />}
        <h3 className={`text-2xl font-bold mb-2 ${plan.popular ? 'text-white' : 'text-gray-900'}`}>
          {plan.name}
        </h3>
        <p className={`text-sm ${plan.popular ? 'text-white/90' : 'text-gray-600'}`}>
          {plan.description}
        </p>
      </div>

      <div className="text-center mb-6">
        <div className={`text-5xl font-bold mb-2 ${plan.popular ? 'text-white' : 'text-gray-900'}`}>
          {priceLabel}
        </div>
        {savings && billingCycle === 'yearly' && (
          <div className="text-sm font-semibold text-green-400">{savings}</div>
        )}
        {!isFree && (
          <div className={`text-sm ${plan.popular ? 'text-white/80' : 'text-gray-500'}`}>
            Billed {billingCycle}
          </div>
        )}
      </div>

      <ul className="space-y-3 mb-8">
        {plan.features.map((feature, idx) => (
          <li key={idx} className="flex items-start gap-2">
            <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${plan.popular ? 'text-green-300' : 'text-green-500'}`} />
            <span className={`text-sm ${plan.popular ? 'text-white' : 'text-gray-700'}`}>
              {feature}
            </span>
          </li>
        ))}
      </ul>

      <button
        className={`w-full py-3 rounded-lg font-bold transition-all ${
          plan.popular
            ? 'bg-white text-purple-600 hover:bg-gray-100'
            : isFree
            ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            : 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700'
        }`}
      >
        {isFree ? 'Current Plan' : isCurrentUser ? 'Upgrade Now' : 'Get Started'}
      </button>
    </div>
  );
}

interface JobBoostCardProps {
  name: string;
  price: string;
  duration: string;
  multiplier: string;
  features: string[];
  color: string;
  popular?: boolean;
}

function JobBoostCard({ name, price, duration, multiplier, features, color, popular }: JobBoostCardProps) {
  return (
    <div className={`relative rounded-xl p-6 bg-white border-2 ${popular ? 'border-purple-400 shadow-xl' : 'border-gray-200'} hover:shadow-xl transition-all`}>
      {popular && (
        <div className="absolute -top-3 right-4">
          <div className="px-3 py-1 bg-purple-600 text-white text-xs font-bold rounded-full">
            POPULAR
          </div>
        </div>
      )}

      <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${color} flex items-center justify-center mb-4`}>
        <TrendingUp className="w-6 h-6 text-white" />
      </div>

      <h3 className="text-xl font-bold text-gray-900 mb-2">{name}</h3>
      <div className="mb-4">
        <span className="text-3xl font-bold text-gray-900">${price}</span>
        <span className="text-gray-600 ml-2">/ {duration}</span>
      </div>
      <div className={`inline-block px-3 py-1 rounded-full bg-gradient-to-r ${color} text-white text-sm font-semibold mb-4`}>
        {multiplier} Visibility
      </div>

      <ul className="space-y-2 mb-6">
        {features.map((feature, idx) => (
          <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
            <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
            {feature}
          </li>
        ))}
      </ul>

      <button className={`w-full py-3 rounded-lg font-bold text-white bg-gradient-to-r ${color} hover:opacity-90 transition-opacity`}>
        Boost This Job
      </button>
    </div>
  );
}

interface FAQItemProps {
  question: string;
  answer: string;
}

function FAQItem({ question, answer }: FAQItemProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="bg-white rounded-lg border-2 border-gray-200 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 text-left font-semibold text-gray-900 hover:bg-gray-50 transition-colors flex items-center justify-between"
      >
        {question}
        <span className={`transform transition-transform ${isOpen ? 'rotate-180' : ''}`}>
          ▼
        </span>
      </button>
      {isOpen && (
        <div className="px-6 py-4 bg-gray-50 text-gray-700 border-t border-gray-200">
          {answer}
        </div>
      )}
    </div>
  );
}
